import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { fetchReports } from "../../../api/service/report";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  FunnelIcon,
  XCircleIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { Form, Formik } from "formik";
import { PMField } from "../PMField";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { createReport } from "../../../api/service/report";

/**
 * Modern Report Selector Component
 * 
 * Features:
 * - Fast report fetching with debounced search
 * - Customer filtering with toggle button
 * - Multiple selection support
 * - Responsive design for mobile and desktop
 * - Compact and user-friendly
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the selector is open
 * @param {Array} props.selectedReports - Array of selected report objects
 * @param {Function} props.onChange - Callback when selection changes (receives array of reports)
 * @param {Function} props.onClose - Callback when selector should close
 * @param {boolean} props.multiple - Allow multiple selection (default: true)
 * @param {number} props.customerId - Optional customer ID to filter by
 * @param {Array} props.filters - Additional filters to apply
 */
export default function ModernReportSelector({
  open = false,
  selectedReports = [],
  onChange,
  onClose,
  multiple = true,
  customerId = null,
  filters = [],
  showHeader = true,
}) {
  const currentUser = useSelector((state) => state.session.currentUser);
  const canCreateReportRole = useUserHasAccess({ role: "reports", action: "create" });
  const canEditReportsRole = useUserHasAccess({ role: "edit_reports" });
  const canCreateReport = canCreateReportRole || canEditReportsRole;
  const [searchQuery, setSearchQuery] = useState("");
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [filterByCustomer, setFilterByCustomer] = useState(
    customerId ? true : false
  );
  const [selectedIds, setSelectedIds] = useState(
    new Set(selectedReports.map((r) => r.id))
  );

  const perPage = 20;

  // Update selectedIds when selectedReports prop changes
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(selectedReports.map((r) => r.id)));
    }
  }, [selectedReports, open]);

  // Update filterByCustomer when customerId prop changes
  useEffect(() => {
    if (customerId) {
      setFilterByCustomer(true);
    } else {
      setFilterByCustomer(false);
    }
  }, [customerId]);

  // Build filters array - combine customer filter with additional filters
  const activeFilters = useMemo(() => {
    const filterArray = [...filters];
    
    if (filterByCustomer && customerId) {
      filterArray.push({ name: "customer", value: customerId.toString() });
    }
    
    return filterArray;
  }, [filterByCustomer, customerId, filters]);
  
  // Create a stable string key for filters to avoid infinite loops
  // This ensures useEffect only triggers when filter values actually change
  const filtersKey = useMemo(() => {
    return JSON.stringify(
      activeFilters.map(f => ({ name: f.name, value: String(f.value) })).sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [activeFilters]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCurrentPage(1);
      setError(null);
      setShowFilters(false);
      setReports([]); // Clear previous search results
    } else {
      // Reset when closed
      setReports([]);
    }
  }, [open]);

  // Create search function that will be debounced
  const searchFunction = useCallback(async (query, page, currentFilters) => {
    setIsLoading(true);
    setError(null);

    try {
      // Convert filters array to object format expected by API
      const filtersObj = {};
      if (currentFilters && currentFilters.length > 0) {
        currentFilters.forEach((filter, index) => {
          filtersObj[index] = {
            name: filter.name,
            value: filter.value,
          };
        });
      }

      const params = {
        query: query || undefined,
        page: page,
        per_page: perPage,
        sortBy: "updated_at",
        sort: "desc",
        fields: "id,displayTitle,customer,delivery_address",
        ...(Object.keys(filtersObj).length > 0 ? { filters: filtersObj } : {}),
      };

      const response = await fetchReports(params);

      if (response && response.data) {
        if (page === 1) {
          setReports(response.data);
        } else {
          setReports((prev) => [...prev, ...response.data]);
        }

        setTotalCount(response.total_count || response.data.length);
        setHasMore(
          response.data.length === perPage &&
            (page * perPage < (response.total_count || 0))
        );
      } else {
        setReports([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching reports:", err);
      setError("Kunne ikke laste rapporter");
      setReports([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search function - use useRef to avoid recreating on every render
  const debouncedSearchRef = useRef(debounce(searchFunction, 300));

  // Update debounced function when searchFunction changes
  useEffect(() => {
    // Cancel previous debounced calls
    debouncedSearchRef.current.cancel();
    // Create new debounced function
    debouncedSearchRef.current = debounce(searchFunction, 300);
  }, [searchFunction]);

  // Fetch reports when search query or filters change
  // Use filtersKey instead of activeFilters to avoid infinite loops
  useEffect(() => {
    if (!open) {
      // Cancel any pending searches when dialog closes
      debouncedSearchRef.current.cancel();
      setIsLoading(false);
      return;
    }
    
    // Always search, even if query is empty (to show filtered results)
    setCurrentPage(1);
    debouncedSearchRef.current(searchQuery || "", 1, activeFilters);

    return () => {
      debouncedSearchRef.current.cancel();
    };
  }, [searchQuery, open, filtersKey]); // Use filtersKey instead of activeFilters to avoid infinite loops

  // Load more reports
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      debouncedSearchRef.current(searchQuery || "", nextPage, activeFilters);
    }
  }, [isLoading, hasMore, currentPage, searchQuery, filtersKey]);

  // Toggle report selection
  const toggleReport = useCallback(
    (report) => {
      const newSelectedIds = new Set(selectedIds);

      if (newSelectedIds.has(report.id)) {
        newSelectedIds.delete(report.id);
      } else {
        if (!multiple) {
          newSelectedIds.clear();
        }
        newSelectedIds.add(report.id);
      }

      setSelectedIds(newSelectedIds);

      // Get selected reports - prioritize reports from current list (they have full data)
      const selectedFromCurrent = reports
        .filter((r) => newSelectedIds.has(r.id));
      const selectedFromPrevious = selectedReports
        .filter(
          (r) =>
            newSelectedIds.has(r.id) &&
            !reports.find((rep) => rep.id === r.id)
        );
      const selected = [...selectedFromCurrent, ...selectedFromPrevious];

      if (onChange) {
        onChange(selected);
      }

      // Auto-close for single selection
      if (!multiple && newSelectedIds.size > 0 && onClose) {
        setTimeout(() => {
          onClose();
        }, 300);
      }
    },
    [
      selectedIds,
      reports,
      selectedReports,
      multiple,
      onChange,
      onClose,
    ]
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    if (onChange) {
      onChange([]);
    }
  }, [onChange]);

  // Check if report is selected
  const isSelected = useCallback(
    (reportId) => {
      return selectedIds.has(reportId);
    },
    [selectedIds]
  );

  // Toggle customer filter
  const toggleCustomerFilter = useCallback(() => {
    setFilterByCustomer((prev) => !prev);
    setCurrentPage(1); // Reset to first page when filter changes
  }, []);

  if (!open) return null;

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-none sm:rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      {showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {multiple ? "Velg rapporter" : "Velg rapport"}
            </h3>
            <div className="flex items-center space-x-2">
              {canCreateReport && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="p-1.5 rounded-md text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors"
                  title="Legg til ny rapport"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              )}
              {customerId && (
                <button
                  onClick={toggleCustomerFilter}
                  className={`p-1.5 rounded-md transition-colors ${
                    filterByCustomer
                      ? "text-blue-600 bg-blue-50 hover:bg-blue-100"
                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                  }`}
                  title={filterByCustomer ? "Fjern kundefilter" : "Filtrer på kunde"}
                >
                  <FunnelIcon className="h-5 w-5" />
                </button>
              )}
              {selectedIds.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title="Fjern alle valg"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  title="Lukk"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter rapporter..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Selected count and filter indicator */}
          <div className="mt-2 flex items-center justify-between">
            {selectedIds.size > 0 && (
              <div className="text-sm text-gray-600">
                {selectedIds.size} {selectedIds.size === 1 ? "rapport" : "rapporter"} valgt
              </div>
            )}
            {customerId && filterByCustomer && (
              <div className="text-xs text-blue-600 flex items-center gap-1">
                <FunnelIcon className="h-3 w-3" />
                Filtrert på kunde
              </div>
            )}
          </div>
        </div>
      )}
      
      {!showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            {/* Search Bar */}
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Søk etter rapporter..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            {customerId && (
              <button
                onClick={toggleCustomerFilter}
                className={`p-1.5 rounded-md transition-colors ${
                  filterByCustomer
                    ? "text-blue-600 bg-blue-50 hover:bg-blue-100"
                    : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                }`}
                title={filterByCustomer ? "Fjern kundefilter" : "Filtrer på kunde"}
              >
                <FunnelIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Report List */}
      <div className="flex-1 overflow-y-auto max-h-[600px]">
        {isLoading && reports.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Laster rapporter...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        ) : reports.length === 0 && !isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-gray-500">
                {searchQuery || filterByCustomer
                  ? "Ingen rapporter funnet"
                  : "Søk etter rapporter for å begynne"}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {reports.slice(0, 20).map((report) => {
              const selected = isSelected(report.id);
              
              return (
                <div
                  key={report.id}
                  className={`w-full p-2 sm:p-3 border-b border-gray-100 last:border-b-0 ${
                    selected ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                  } ${!multiple ? "cursor-pointer hover:bg-gray-50" : ""}`}
                  onClick={!multiple ? () => toggleReport(report) : undefined}
                >
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 mt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleReport(report);
                        }}
                        className="focus:outline-none"
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            selected
                              ? "bg-blue-600 border-blue-600"
                              : "border-gray-300 hover:border-blue-400"
                          }`}
                        >
                          {selected && (
                            <CheckIcon className="h-4 w-4 text-white" />
                          )}
                        </div>
                      </button>
                    </div>

                    {/* Report Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {report.displayTitle || `Rapport #${report.id}`}
                      </p>
                      {report.customer && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Kunde: {report.customer.displayTitle || report.customer.name}
                        </p>
                      )}
                      {report.delivery_address && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Leveringsadresse: {report.delivery_address.displayTitle}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && !isLoading && reports.length < 20 && (
          <div className="p-4 text-center border-t border-gray-200">
            <button
              onClick={loadMore}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Last flere rapporter
            </button>
          </div>
        )}

        {isLoading && reports.length > 0 && (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        )}
      </div>

      {/* Create Report Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCreateForm(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Legg til ny rapport
                  </h3>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <Formik
                  initialValues={{
                    name: "",
                    report_number: "",
                    customer_id: customerId || null,
                    active: true,
                  }}
                  validationSchema={Yup.object({
                    name: Yup.string().required("Navn er påkrevd"),
                  })}
                  onSubmit={async (values, { setSubmitting, setErrors }) => {
                    setIsCreating(true);
                    try {
                      const response = await createReport(values);
                      if (response && response.id) {
                        toast.success("Rapport opprettet!");
                        // Add the new report to the list and select it
                        const newReport = response;
                        setReports((prev) => [newReport, ...prev]);
                        const newSelectedIds = new Set(selectedIds);
                        if (!multiple) {
                          newSelectedIds.clear();
                        }
                        newSelectedIds.add(newReport.id);
                        setSelectedIds(newSelectedIds);
                        
                        if (onChange) {
                          onChange([newReport]);
                        }
                        
                        setShowCreateForm(false);
                        
                        // Auto-close for single selection
                        if (!multiple && onClose) {
                          setTimeout(() => {
                            onClose();
                          }, 300);
                        }
                      } else {
                        throw new Error("Kunne ikke opprette rapport");
                      }
                    } catch (err) {
                      console.error("Error creating report:", err);
                      toast.error("Kunne ikke opprette rapport");
                      setErrors({ submit: err.message });
                    } finally {
                      setIsCreating(false);
                      setSubmitting(false);
                    }
                  }}
                >
                  {(formik) => (
                    <Form>
                      <div className="space-y-4">
                        <PMField
                          name="name"
                          label="Navn"
                          type="text"
                          required
                          {...formik}
                        />
                        <PMField
                          name="report_number"
                          label="Rapportnummer"
                          type="text"
                          {...formik}
                        />
                        <div className="flex justify-end space-x-3 pt-4">
                          <button
                            type="button"
                            onClick={() => setShowCreateForm(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Avbryt
                          </button>
                          <button
                            type="submit"
                            disabled={isCreating || formik.isSubmitting}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isCreating ? "Oppretter..." : "Opprett"}
                          </button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

